import React from "react";
import { cn } from "utils";

interface SwitchProps {
  /** 是否选中 */
  checked: boolean;
  /** 状态改变回调 */
  onChange?: (checked: boolean) => void;
  /** 是否禁用 */
  disabled?: boolean;
  /** 自定义类名 */
  className?: string;
  /** 尺寸 */
  size?: "sm" | "md";
}

const Switch: React.FC<SwitchProps> = ({
  checked,
  onChange,
  disabled = false,
  className,
  size = "md",
}) => {
  const handleClick = () => {
    if (!disabled && onChange) {
      onChange(!checked);
    }
  };

  const sizeConfig = {
    sm: {
      track: "w-9 h-5",
      thumb: "w-4 h-4",
      translate: "translate-x-4",
    },
    md: {
      track: "w-11 h-6",
      thumb: "w-4 h-4",
      translate: "translate-x-5",
    },
  };

  const config = sizeConfig[size];

  return (
    <button
      aria-label="Switch"
      data-openreplay-label="Switch Button"
      id="btn-switch"
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={handleClick}
      className={cn(
        "flex items-center relative inline-flex shrink-0 cursor-pointer rounded-full p-1 transition-all duration-200 ease-in-out focus:outline-none border-1",
        config.track,
        {
          // 开启状态 - 品牌色背景，hover 时变为 70% 透明度，透明边框避免动画闪烁
          "bg-background_brand_overlay100 hover:bg-background_brand_overlay70 border-transparent":
            checked && !disabled,
          // 开启禁用状态 - 10% 白色透明背景，透明边框
          "bg-background_white_overlay10 border-transparent cursor-not-allowed":
            checked && disabled,
          // 关闭状态 - 透明背景 + 边框
          "bg-transparent border-border_solid": !checked && !disabled,
          // 关闭禁用状态 - 透明背景 + 边框 + 50% 整体透明度
          "bg-transparent border-border_solid opacity-50 cursor-not-allowed":
            !checked && disabled,
        },
        className,
      )}
    >
      <span
        className={cn(
          "pointer-events-none inline-block rounded-full transition-all duration-200 ease-in-out",
          config.thumb,
          {
            [config.translate]: checked,
            "translate-x-0": !checked,
            // 开启状态 - 深色圆圈（反色主要内容）
            "bg-content_inverse_primary": checked && !disabled,
            // 开启禁用状态 - 灰色圆圈（四级内容色）
            "bg-content_quaternary": checked && disabled,
            // 关闭状态 - 三级内容色，hover 时 70% 透明度
            "bg-content_tertiary hover:opacity-70": !checked && !disabled,
            // 关闭禁用状态 - 三级内容色（透明度由父元素控制）
            "bg-content_tertiary": !checked && disabled,
          },
        )}
      />
    </button>
  );
};

export default Switch;
